import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch, readJsonResponse } from '../api/client';
import RisksTopBar from '../components/RisksTopBar';
import { getAvailableRiskTypes, getRiskShortLabel } from '../constants/riskTypes';

const QUEUE_DEBOUNCE_MS = 600;
const QUEUE_REFRESH_MS = 30_000;
const HISTORY_PAGE_SIZES = [15, 25, 50];
const HISTORY_DEFAULT_PAGE_SIZE = 25;

const STATUS_LABEL = {
  sent: 'отправлен',
  delivered: 'доставлен',
  opened: 'прочитан',
  failed: 'ошибка',
  no_token: 'нет токена',
  no_user: 'нет user',
  queued: 'очередь',
};

const inputClass =
  'w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-[12px] text-slate-900 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';

function statusClass(status) {
  if (status === 'opened') {
    return 'bg-emerald-600/20 text-emerald-900 dark:text-emerald-200';
  }
  if (status === 'delivered') {
    return 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300';
  }
  if (status === 'sent') {
    return 'bg-sky-500/15 text-sky-800 dark:text-sky-300';
  }
  if (status === 'no_token' || status === 'no_user') {
    return 'bg-amber-500/15 text-amber-800 dark:text-amber-300';
  }
  return 'bg-red-500/15 text-red-800 dark:text-red-300';
}

function emptyRule(riskNumber) {
  return {
    risk_number: riskNumber,
    enabled: false,
    threshold: '',
    message_title: '',
    message_body: '',
    remind_count: '0',
    remind_interval_hours: '6',
  };
}

function mapRuleRow(n, row) {
  return {
    risk_number: n,
    enabled: Boolean(row.enabled),
    threshold: row.threshold == null ? '' : String(row.threshold),
    message_title: row.message_title || '',
    message_body: row.message_body || '',
    remind_count: row.remind_count == null ? '0' : String(row.remind_count),
    remind_interval_hours:
      row.remind_interval_hours == null ? '6' : String(row.remind_interval_hours),
  };
}

function formatTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(value);
  }
}

function formatDurationMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1000) return `${Math.round(n)}мс`;
  const sec = Math.round(n / 1000);
  if (sec < 60) return `${sec}с`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `${m}м ${s}с` : `${m}м`;
}

/** Compact per-risk line from tick detail.by_risk. */
function formatTickByRisk(byRisk) {
  if (!byRisk || typeof byRisk !== 'object') return '';
  return Object.keys(byRisk)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => {
      const row = byRisk[key] || {};
      const bits = [`#${key}`];
      if (row.sends) bits.push(`sent ${row.sends}`);
      if (row.initials) bits.push(`1й ${row.initials}`);
      if (row.reminds) bits.push(`повт ${row.reminds}`);
      if (row.suppressed) bits.push(`policy ${row.suppressed}`);
      if (row.logged && !row.sends) bits.push(`log ${row.logged}`);
      return bits.join(' ');
    })
    .filter(Boolean)
    .join(' · ');
}

/** History row → title/body as logged (sent or failed attempt). */
function PushSendDetailModal({ row, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!row || typeof document === 'undefined') return null;

  const statusLabel = STATUS_LABEL[row.status] || row.status || '—';
  const kindLabel = row.kind === 'remind' ? 'повтор' : 'первый';

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-950/60 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Текст пуша"
    >
      <button type="button" aria-label="Закрыть фон" className="fixed inset-0 cursor-default" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-4 shadow-lg dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Текст пуша</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300"
          >
            Закрыть
          </button>
        </div>
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12px] text-slate-700 dark:text-slate-200">
          <dt className="text-slate-500">Время</dt>
          <dd>{formatTime(row.created_at)}</dd>
          <dt className="text-slate-500">Serial</dt>
          <dd className="font-mono">{row.serial_number || row.device_id || '—'}</dd>
          <dt className="text-slate-500">User</dt>
          <dd>{row.user_id ?? '—'}</dd>
          <dt className="text-slate-500">Score</dt>
          <dd>{row.score ?? '—'}</dd>
          <dt className="text-slate-500">Тип</dt>
          <dd>{kindLabel}</dd>
          <dt className="text-slate-500">Статус</dt>
          <dd>
            <span className={`inline-flex rounded px-1 py-0.5 ${statusClass(row.status)}`}>{statusLabel}</span>
          </dd>
          {row.detail ? (
            <>
              <dt className="text-slate-500">Detail</dt>
              <dd className="break-all text-slate-600 dark:text-slate-300">{row.detail}</dd>
            </>
          ) : null}
          {row.notification_id ? (
            <>
              <dt className="text-slate-500">NS id</dt>
              <dd className="break-all font-mono text-[11px] text-slate-500">{row.notification_id}</dd>
            </>
          ) : null}
        </dl>
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/60">
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">Заголовок</p>
          <p className="mt-0.5 whitespace-pre-wrap text-[13px] text-slate-900 dark:text-slate-100">
            {String(row.title || '').trim() || '—'}
          </p>
          <p className="mt-2 text-[10px] font-medium uppercase tracking-wide text-slate-500">Текст</p>
          <p className="mt-0.5 whitespace-pre-wrap text-[13px] text-slate-800 dark:text-slate-200">
            {String(row.body || '').trim() || '—'}
          </p>
        </div>
        <p className="mt-2 text-[10px] text-slate-400">
          Текст из лога попытки (в т.ч. если не ушёл: нет токена / нет user / ошибка).
        </p>
      </div>
    </div>,
    document.body,
  );
}

function ruleCanSend(draft) {
  if (!draft?.enabled) return false;
  if (draft.threshold === '' || draft.threshold == null) return false;
  return Boolean(String(draft.message_title || '').trim() && String(draft.message_body || '').trim());
}

function queueProgress(queue, { canSend = true } = {}) {
  const above = Number(queue?.above) || 0;
  const handled = Number(queue?.handled) || 0;
  const remaining = Number(queue?.remaining) || 0;
  const sent = Number(queue?.sent) || 0;
  const pct = above > 0 ? Math.min(100, Math.round((handled / above) * 100)) : 100;
  // Empty title/body → worker skips the rule; never spin forever on a stuck remainder.
  const spinning = Boolean(canSend) && remaining > 0 && above > 0;
  const blocked = !canSend && remaining > 0 && above > 0;
  return { above, handled, remaining, sent, pct, spinning, blocked };
}

function HistorySpinner({ className = 'h-3.5 w-3.5' }) {
  return (
    <span
      className={`inline-block animate-spin rounded-full border-2 border-slate-300 border-t-sky-600 dark:border-slate-600 dark:border-t-sky-400 ${className}`}
      aria-hidden="true"
    />
  );
}

function HistoryTableSkeleton({ rows = 6 }) {
  return (
    <tbody aria-busy="true" aria-label="Загрузка истории">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={`sk-${rowIndex}`} className="border-t border-slate-100 dark:border-slate-900">
          {Array.from({ length: 9 }).map((__, colIndex) => (
            <td key={`sk-${rowIndex}-${colIndex}`} className="px-1.5 py-1.5">
              <div
                className={`h-2.5 animate-pulse rounded bg-slate-200 dark:bg-slate-800 ${
                  (rowIndex + colIndex) % 3 === 0 ? 'w-16' : (rowIndex + colIndex) % 3 === 1 ? 'w-12' : 'w-20'
                }`}
              />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

function HistoryPagination({ page, pageSize, total, visibleCount, isLoading, onPageChange, onPageSizeChange }) {
  const totalPages = Math.max(1, Math.ceil(Number(total) / Math.max(1, Number(pageSize) || 1)));
  const safePage = Math.min(Math.max(Number(page) || 1, 1), totalPages);
  const rangeStart = total === 0 || visibleCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = total === 0 || visibleCount === 0 ? 0 : rangeStart + visibleCount - 1;
  const btn =
    'min-h-7 rounded border border-slate-300 px-1.5 text-[11px] text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-200';

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/80 px-1.5 py-1 dark:border-slate-800 dark:bg-slate-900/50">
      <p className="text-[10px] tabular-nums text-slate-500 dark:text-slate-400">
        {rangeStart}–{rangeEnd} из {total}
      </p>
      <div className="flex flex-wrap items-center gap-1">
        <select
          aria-label="Размер страницы истории"
          value={pageSize}
          disabled={isLoading}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          className="h-7 rounded border border-slate-300 bg-white px-1 text-[11px] text-slate-800 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
        >
          {HISTORY_PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <button type="button" aria-label="В начало" disabled={isLoading || safePage <= 1} onClick={() => onPageChange(1)} className={btn}>
          «
        </button>
        <button
          type="button"
          aria-label="Назад"
          disabled={isLoading || safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          className={btn}
        >
          ‹
        </button>
        <span className="min-w-[3rem] px-1 text-center text-[11px] tabular-nums text-slate-700 dark:text-slate-300">
          {safePage}/{totalPages}
        </span>
        <button
          type="button"
          aria-label="Вперёд"
          disabled={isLoading || safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
          className={btn}
        >
          ›
        </button>
        <button
          type="button"
          aria-label="В конец"
          disabled={isLoading || safePage >= totalPages}
          onClick={() => onPageChange(totalPages)}
          className={btn}
        >
          »
        </button>
      </div>
    </div>
  );
}

/** Warning when push broadcast worker failed / stuck / stale. */
function workerWarningText(status) {
  const worker = status?.worker;
  if (!worker) return null;
  if (worker.health && worker.health.ok === false && worker.health.message) {
    return String(worker.health.message);
  }
  if (worker.status === 'stopped') {
    return 'Worker рассылки остановлен';
  }
  const last = worker.last_run;
  if (last?.phase === 'error') {
    return `Worker рассылки упал: ${last.error || 'ошибка'}`;
  }
  if (worker.running && last?.at) {
    const ageMs = Date.now() - new Date(last.at).getTime();
    if (Number.isFinite(ageMs) && ageMs > 3 * 60 * 1000) {
      return 'Worker рассылки завис — тик не завершился';
    }
  }
  if (last?.at && !worker.running) {
    const ageMs = Date.now() - new Date(last.at).getTime();
    const interval = Number(worker.interval_ms) || 600_000;
    if (Number.isFinite(ageMs) && ageMs > interval * 2 + 120_000) {
      return 'Worker рассылки давно не выполнял тик';
    }
  }
  return null;
}

function QueueStatusBar({ queue, isActiveLane, canSend = true }) {
  if (!queue) {
    return (
      <div className="mt-1.5 flex h-5 items-center gap-1.5 text-[10px] text-slate-400">
        <span className="inline-block h-3 w-3 animate-spin rounded-full border border-slate-300 border-t-slate-600 dark:border-slate-600 dark:border-t-slate-300" />
        очередь…
      </div>
    );
  }
  const { above, handled, remaining, sent, pct, spinning, blocked } = queueProgress(queue, { canSend });
  const barColor = blocked
    ? 'bg-slate-400 dark:bg-slate-500'
    : spinning
      ? 'bg-amber-500'
      : above === 0
        ? 'bg-slate-300 dark:bg-slate-600'
        : 'bg-emerald-500';
  return (
    <div
      className="mt-1.5 flex flex-col gap-0.5"
      title="Сейчас ≥ порога · обработано (latch) · успешный sent · осталось без latch"
    >
      <div className="flex items-center gap-2">
        {spinning ? (
          <span
            className={[
              'inline-block h-3 w-3 shrink-0 animate-spin rounded-full border-2',
              isActiveLane
                ? 'border-amber-200 border-t-amber-600 dark:border-amber-900 dark:border-t-amber-300'
                : 'border-slate-200 border-t-slate-500 dark:border-slate-700 dark:border-t-slate-300',
            ].join(' ')}
            aria-label={isActiveLane ? 'очередь воркера здесь' : 'есть остаток'}
          />
        ) : blocked ? (
          <span
            className="inline-block h-3 w-3 shrink-0 rounded-full bg-amber-500/90"
            aria-label="очередь стоит — нет текста push"
          />
        ) : (
          <span
            className="inline-block h-3 w-3 shrink-0 rounded-full bg-emerald-500/80"
            aria-label="очередь пуста"
          />
        )}
        <div className="h-1.5 min-w-[6rem] flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className={`h-full rounded-full transition-[width] duration-500 ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="shrink-0 tabular-nums text-[10px] text-slate-600 dark:text-slate-300">
          <span className="font-medium">{pct}%</span>
          <span className="text-slate-400"> · </span>
          {handled}/{above}
          <span className="text-slate-400"> · </span>
          ушло {sent}
          <span className="text-slate-400"> · </span>
          ост.{' '}
          <span
            className={
              remaining > 0
                ? 'font-medium text-amber-700 dark:text-amber-300'
                : 'text-emerald-700 dark:text-emerald-300'
            }
          >
            {remaining}
          </span>
          {isActiveLane && spinning ? (
            <span className="ml-1 text-amber-700 dark:text-amber-300">← тик</span>
          ) : null}
        </span>
      </div>
      {blocked ? (
        <p className="pl-5 text-[10px] text-amber-800 dark:text-amber-200">
          Нет заголовка/текста — worker не шлёт, очередь не двигается
        </p>
      ) : null}
    </div>
  );
}

export default function BroadcastPage({ panelActive = true, activeTab, onTabChange } = {}) {
  const [rules, setRules] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [status, setStatus] = useState(null);
  const [openRisk, setOpenRisk] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(HISTORY_DEFAULT_PAGE_SIZE);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [pushDetail, setPushDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const savingLock = useRef(null);
  const [error, setError] = useState('');
  const [queueByRisk, setQueueByRisk] = useState({});
  const [ticks, setTicks] = useState([]);
  const [ticksOpen, setTicksOpen] = useState(false);
  const [ticksLoading, setTicksLoading] = useState(false);
  const queueTimers = useRef({});
  const initialLoadRef = useRef(false);

  const applyQueueItems = useCallback((items) => {
    const next = {};
    for (const row of items || []) {
      const n = Number(row.risk_number);
      if (!Number.isFinite(n)) continue;
      next[n] = {
        threshold: row.threshold == null ? null : Number(row.threshold),
        above: Number(row.above) || 0,
        handled: Number(row.handled) || 0,
        remaining: Number(row.remaining) || 0,
        sent: Number(row.sent) || 0,
      };
    }
    setQueueByRisk((prev) => ({ ...prev, ...next }));
  }, []);

  const loadTicks = useCallback(async () => {
    setTicksLoading(true);
    try {
      const res = await apiFetch('/api/push-broadcast/ticks?limit=30&page=1');
      const json = await readJsonResponse(res, 'push-broadcast/ticks');
      setTicks(Array.isArray(json.items) ? json.items : []);
    } catch {
      /* keep last ticks */
    } finally {
      setTicksLoading(false);
    }
  }, []);

  const loadQueueAll = useCallback(async () => {
    try {
      const [queueRes, statusRes] = await Promise.all([
        apiFetch('/api/push-broadcast/queue-stats'),
        apiFetch('/api/push-broadcast/status'),
      ]);
      const json = await readJsonResponse(queueRes, 'push-broadcast/queue-stats');
      applyQueueItems(Array.isArray(json.items) ? json.items : []);
      try {
        const statusJson = await readJsonResponse(statusRes, 'push-broadcast/status');
        setStatus(statusJson);
      } catch {
        /* keep last status */
      }
      if (ticksOpen) {
        await loadTicks();
      }
    } catch {
      /* keep last known queue stats */
    }
  }, [applyQueueItems, loadTicks, ticksOpen]);

  const loadQueueOne = useCallback(
    async (riskNumber, threshold) => {
      const n = Number(riskNumber);
      if (!Number.isFinite(n)) return;
      if (threshold === '' || threshold == null) {
        setQueueByRisk((prev) => ({
          ...prev,
          [n]: { threshold: null, above: 0, handled: 0, remaining: 0, sent: 0 },
        }));
        return;
      }
      const t = Number(threshold);
      if (!Number.isFinite(t) || t < 0 || t > 100) return;
      try {
        const res = await apiFetch(
          `/api/push-broadcast/rules/${n}/queue-stats?threshold=${encodeURIComponent(String(t))}`,
        );
        const json = await readJsonResponse(res, 'push-broadcast/queue-stats');
        applyQueueItems([json]);
      } catch {
        /* ignore */
      }
    },
    [applyQueueItems],
  );

  const scheduleQueueOne = useCallback(
    (riskNumber, threshold) => {
      const n = Number(riskNumber);
      if (queueTimers.current[n]) {
        clearTimeout(queueTimers.current[n]);
      }
      queueTimers.current[n] = setTimeout(() => {
        loadQueueOne(n, threshold);
      }, QUEUE_DEBOUNCE_MS);
    },
    [loadQueueOne],
  );

  const load = useCallback(async () => {
    setError('');
    try {
      const [rulesRes, statusRes] = await Promise.all([
        apiFetch('/api/push-broadcast/rules'),
        apiFetch('/api/push-broadcast/status'),
      ]);
      const rulesJson = await readJsonResponse(rulesRes, 'push-broadcast/rules');
      const statusJson = await readJsonResponse(statusRes, 'push-broadcast/status');
      const items = Array.isArray(rulesJson.items) ? rulesJson.items : [];
      const byNumber = new Map(items.map((row) => [Number(row.risk_number), row]));
      const merged = getAvailableRiskTypes().map((risk) => {
        const n = Number(risk.id);
        const row = byNumber.get(n);
        return row ? mapRuleRow(n, row) : emptyRule(n);
      });
      setRules(merged);
      setDrafts(Object.fromEntries(merged.map((row) => [row.risk_number, { ...row }])));
      setStatus(statusJson);
      await loadQueueAll();
      return true;
    } catch (err) {
      setError(err.message || 'Не удалось загрузить правила');
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadQueueAll]);

  useEffect(() => {
    if (panelActive === false) {
      return undefined;
    }
    if (!initialLoadRef.current) {
      load().then((ok) => {
        if (ok) {
          initialLoadRef.current = true;
        }
      });
    }
    const timer = setInterval(() => {
      loadQueueAll();
    }, QUEUE_REFRESH_MS);
    return () => {
      clearInterval(timer);
      Object.values(queueTimers.current).forEach((id) => clearTimeout(id));
      queueTimers.current = {};
    };
  }, [load, loadQueueAll, panelActive]);

  const loadHistory = useCallback(async (riskNumber, page, pageSize) => {
    setHistoryLoading(true);
    try {
      const qs = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      const res = await apiFetch(`/api/push-broadcast/rules/${riskNumber}/history?${qs}`);
      const json = await readJsonResponse(res, 'push-broadcast/history');
      setHistory(Array.isArray(json.items) ? json.items : []);
      setHistoryTotal(Number(json.total) || 0);
      if (json.page != null) setHistoryPage(Number(json.page) || page);
    } catch (err) {
      setError(err.message || 'История недоступна');
      setHistory([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (openRisk == null) {
      setHistory([]);
      setHistoryTotal(0);
      setHistoryLoading(false);
      return undefined;
    }
    loadHistory(openRisk, historyPage, historyPageSize);
    return undefined;
  }, [openRisk, historyPage, historyPageSize, loadHistory]);

  function toggleHistory(riskNumber) {
    setOpenRisk((prev) => {
      if (prev === riskNumber) return null;
      setHistory([]);
      setHistoryTotal(0);
      setHistoryPage(1);
      setHistoryLoading(true);
      return riskNumber;
    });
  }

  async function persistRule(riskNumber, draftOverride = null) {
    const draft = draftOverride || drafts[riskNumber];
    if (!draft) return null;
    if (savingLock.current === riskNumber) return null;
    savingLock.current = riskNumber;
    setSaving(riskNumber);
    setError('');
    try {
      const res = await apiFetch(`/api/push-broadcast/rules/${riskNumber}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: Boolean(draft.enabled),
          threshold: draft.threshold === '' ? null : Number(draft.threshold),
          message_title: draft.message_title,
          message_body: draft.message_body,
          remind_count: draft.remind_count === '' ? 0 : Number(draft.remind_count),
          remind_interval_hours:
            draft.remind_interval_hours === '' ? 6 : Number(draft.remind_interval_hours),
        }),
      });
      const json = await readJsonResponse(res, 'push-broadcast/save');
      const next = mapRuleRow(riskNumber, json);
      setDrafts((prev) => ({ ...prev, [riskNumber]: next }));
      setRules((prev) => prev.map((row) => (row.risk_number === riskNumber ? next : row)));
      scheduleQueueOne(riskNumber, next.threshold);
      return next;
    } catch (err) {
      setError(err.message || 'Сохранение не удалось');
      return null;
    } finally {
      if (savingLock.current === riskNumber) savingLock.current = null;
      setSaving(null);
    }
  }

  async function saveRule(riskNumber) {
    await persistRule(riskNumber);
  }

  async function toggleEnabled(riskNumber, enabled) {
    const draft = drafts[riskNumber];
    if (!draft) return;
    const nextDraft = { ...draft, enabled: Boolean(enabled) };
    setDrafts((prev) => ({ ...prev, [riskNumber]: nextDraft }));
    const saved = await persistRule(riskNumber, nextDraft);
    if (!saved) {
      setDrafts((prev) => ({ ...prev, [riskNumber]: draft }));
    }
  }

  function patchDraft(riskNumber, patch) {
    setDrafts((prev) => {
      const next = { ...prev[riskNumber], ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'threshold')) {
        scheduleQueueOne(riskNumber, next.threshold);
      }
      return { ...prev, [riskNumber]: next };
    });
  }

  const configured = Boolean(status?.configured);
  const lastRun = status?.worker?.last_run;
  const workerWarning = workerWarningText(status);
  const activeLaneRisk = (() => {
    const ordered = [...rules].sort((a, b) => a.risk_number - b.risk_number);
    for (const rule of ordered) {
      const draft = drafts[rule.risk_number] || rule;
      if (!ruleCanSend(draft)) continue;
      const q = queueByRisk[rule.risk_number];
      if (q && queueProgress(q, { canSend: true }).spinning) return rule.risk_number;
    }
    return null;
  })();

  return (
    <div className="mx-auto max-w-5xl space-y-2">
      <RisksTopBar
        meta={
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            Рассылка · Push API:{' '}
            <span className={configured ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}>
              {configured ? 'ok' : 'не настроен'}
            </span>
            {workerWarning ? (
              <span className="font-medium text-red-700 dark:text-red-300"> · worker!</span>
            ) : null}
            {activeLaneRisk != null ? (
              <span className="text-amber-700 dark:text-amber-300">
                {' '}
                · очередь #{activeLaneRisk}
              </span>
            ) : null}
            {lastRun?.at ? (
              <span className="text-slate-400">
                {' '}
                · тик {formatTime(lastRun.at)}
                {lastRun.sends != null ? ` · sent ${lastRun.sends}` : ''}
              </span>
            ) : null}
            <span className="text-slate-400"> · poll 30с</span>
          </span>
        }
      />

      {workerWarning ? (
        <div className="rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          {workerWarning}
        </div>
      ) : null}

      <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 dark:border-slate-800 dark:bg-slate-950/60">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const next = !ticksOpen;
              setTicksOpen(next);
              if (next) {
                loadTicks();
              }
            }}
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-700 hover:border-sky-400 dark:border-slate-600 dark:text-slate-200"
          >
            {ticksOpen ? 'Тики worker ▲' : 'Тики worker'}
            {ticksLoading ? <HistorySpinner className="h-3 w-3" /> : null}
          </button>
          <span className="text-[10px] text-slate-400">
            журнал тиков (не отправки) · poll 30с когда открыт
          </span>
        </div>
        {ticksOpen ? (
          <div className="mt-1.5 overflow-x-auto">
            <table className="min-w-full text-left text-[10px] text-slate-700 dark:text-slate-200">
              <thead className="bg-slate-50 text-slate-500 dark:bg-slate-900/80">
                <tr>
                  <th className="px-1.5 py-1 font-medium">Время</th>
                  <th className="px-1.5 py-1 font-medium">Длит.</th>
                  <th className="px-1.5 py-1 font-medium">Фаза</th>
                  <th className="px-1.5 py-1 font-medium">sent</th>
                  <th className="px-1.5 py-1 font-medium">log</th>
                  <th className="px-1.5 py-1 font-medium">policy↓</th>
                  <th className="px-1.5 py-1 font-medium">fb</th>
                  <th className="px-1.5 py-1 font-medium">флаги</th>
                  <th className="px-1.5 py-1 font-medium">по рискам</th>
                </tr>
              </thead>
              <tbody>
                {ticks.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-1.5 py-2 text-slate-400">
                      {ticksLoading ? 'Загрузка…' : 'Пока пусто — появится после следующего тика'}
                    </td>
                  </tr>
                ) : (
                  ticks.map((row) => {
                    const flags = [
                      row.tick_truncated ? 'trunc' : null,
                      row.feedback_truncated ? 'fb↦' : null,
                      row.max_sends_hit ? 'cap' : null,
                      row.error ? 'err' : null,
                    ].filter(Boolean);
                    const byRisk = formatTickByRisk(row.by_risk);
                    return (
                      <tr key={row.id} className="border-t border-slate-100 dark:border-slate-900">
                        <td className="whitespace-nowrap px-1.5 py-1">{formatTime(row.finished_at)}</td>
                        <td className="whitespace-nowrap px-1.5 py-1 tabular-nums">
                          {formatDurationMs(row.duration_ms)}
                        </td>
                        <td className="whitespace-nowrap px-1.5 py-1">
                          <span
                            className={
                              row.phase === 'error'
                                ? 'text-red-700 dark:text-red-300'
                                : row.phase === 'done'
                                  ? 'text-emerald-700 dark:text-emerald-300'
                                  : 'text-slate-500'
                            }
                            title={row.error || ''}
                          >
                            {row.phase || '—'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-1.5 py-1 tabular-nums font-medium">
                          {row.sends}
                        </td>
                        <td className="whitespace-nowrap px-1.5 py-1 tabular-nums">{row.logged}</td>
                        <td
                          className={[
                            'whitespace-nowrap px-1.5 py-1 tabular-nums',
                            row.suppressed_by_policy > 0
                              ? 'font-medium text-amber-700 dark:text-amber-300'
                              : '',
                          ].join(' ')}
                        >
                          {row.suppressed_by_policy}
                        </td>
                        <td className="whitespace-nowrap px-1.5 py-1 tabular-nums">{row.feedback}</td>
                        <td
                          className="whitespace-nowrap px-1.5 py-1 text-amber-800 dark:text-amber-200"
                          title={row.error || ''}
                        >
                          {flags.length ? flags.join(' · ') : '—'}
                        </td>
                        <td className="max-w-[18rem] truncate px-1.5 py-1 text-slate-500" title={byRisk}>
                          {byRisk || '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="rounded border border-red-300 bg-red-50 px-2 py-1 text-[11px] text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-[11px] text-slate-500">Загрузка…</p>
      ) : (
        <div className="space-y-1.5">
          {rules.map((rule) => {
            const draft = drafts[rule.risk_number] || rule;
            const expanded = openRisk === rule.risk_number;
            const on = Boolean(draft.enabled);
            const busy = saving === rule.risk_number;
            const queue = queueByRisk[rule.risk_number];
            return (
              <section
                key={rule.risk_number}
                className={[
                  'rounded-lg border px-2.5 py-2 transition',
                  on
                    ? 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/60'
                    : 'border-dashed border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-950/30',
                ].join(' ')}
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <h2 className="min-w-[7rem] text-[12px] font-semibold text-slate-900 dark:text-white">
                    #{rule.risk_number} {getRiskShortLabel(rule.risk_number)}
                  </h2>
                  <label className="inline-flex cursor-pointer items-center gap-1 text-[11px] text-slate-600 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={busy}
                      onChange={(event) => toggleEnabled(rule.risk_number, event.target.checked)}
                      className="h-3.5 w-3.5"
                    />
                    {on ? 'вкл' : 'выкл'}
                  </label>
                  <label className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                    ≥
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.1}
                      disabled={busy}
                      value={draft.threshold}
                      onChange={(event) =>
                        patchDraft(rule.risk_number, { threshold: event.target.value })
                      }
                      className={`${inputClass} w-16`}
                      title="Порог score"
                    />
                  </label>
                  <label
                    className="inline-flex items-center gap-1 text-[11px] text-slate-500"
                    title="Повторных отправок после первого (0 = только первый пуш)"
                  >
                    повторы
                    <input
                      type="number"
                      min={0}
                      max={48}
                      step={1}
                      disabled={busy}
                      value={draft.remind_count}
                      onChange={(event) =>
                        patchDraft(rule.risk_number, { remind_count: event.target.value })
                      }
                      className={`${inputClass} w-12`}
                    />
                  </label>
                  <label
                    className="inline-flex items-center gap-1 text-[11px] text-slate-500"
                    title="Часы между повторными отправками"
                  >
                    каждые
                    <input
                      type="number"
                      min={0.1}
                      max={168}
                      step={0.5}
                      disabled={busy}
                      value={draft.remind_interval_hours}
                      onChange={(event) =>
                        patchDraft(rule.risk_number, { remind_interval_hours: event.target.value })
                      }
                      className={`${inputClass} w-14`}
                    />
                    ч
                  </label>
                  <input
                    type="text"
                    maxLength={120}
                    disabled={busy}
                    value={draft.message_title}
                    onChange={(event) =>
                      patchDraft(rule.risk_number, { message_title: event.target.value })
                    }
                    placeholder="Заголовок"
                    className={`${inputClass} min-w-[10rem] flex-1`}
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => saveRule(rule.risk_number)}
                    className="rounded bg-slate-900 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
                  >
                    {busy ? '…' : 'Сохранить'}
                  </button>
                  <button
                    type="button"
                    aria-busy={expanded && historyLoading ? 'true' : undefined}
                    onClick={() => toggleHistory(rule.risk_number)}
                    className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 dark:border-slate-700 dark:text-slate-200"
                  >
                    {expanded && historyLoading ? <HistorySpinner /> : null}
                    {expanded ? 'История ▲' : 'История'}
                  </button>
                </div>

                {draft.threshold !== '' ? (
                  <QueueStatusBar
                    queue={queue}
                    isActiveLane={activeLaneRisk === rule.risk_number}
                    canSend={ruleCanSend(draft)}
                  />
                ) : null}

                <textarea
                  rows={2}
                  maxLength={500}
                  disabled={busy}
                  value={draft.message_body}
                  onChange={(event) =>
                    patchDraft(rule.risk_number, { message_body: event.target.value })
                  }
                  placeholder="Текст push"
                  className={`mt-1.5 ${inputClass}`}
                />

                {expanded ? (
                  <div className="mt-1.5 overflow-hidden rounded border border-slate-200 dark:border-slate-800">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-[10px] text-slate-700 dark:text-slate-200">
                        <thead className="bg-slate-50 text-slate-500 dark:bg-slate-900/80">
                          <tr>
                            <th className="px-1.5 py-1 font-medium">Время</th>
                            <th className="px-1.5 py-1 font-medium">Serial</th>
                            <th className="px-1.5 py-1 font-medium">User</th>
                            <th className="px-1.5 py-1 font-medium">Score</th>
                            <th className="px-1.5 py-1 font-medium">Тип</th>
                            <th className="px-1.5 py-1 font-medium">Статус</th>
                            <th className="px-1.5 py-1 font-medium">Прочитан</th>
                            <th className="px-1.5 py-1 font-medium">Detail</th>
                            <th className="px-1.5 py-1 font-medium" aria-label="Текст пуша" />
                          </tr>
                        </thead>
                        {historyLoading ? (
                          <HistoryTableSkeleton rows={Math.min(historyPageSize, 8)} />
                        ) : (
                          <tbody>
                            {history.length === 0 ? (
                              <tr>
                                <td colSpan={9} className="px-1.5 py-2 text-slate-400">
                                  Пусто
                                </td>
                              </tr>
                            ) : (
                              history.map((row) => (
                                <tr
                                  key={row.id}
                                  className="border-t border-slate-100 dark:border-slate-900"
                                >
                                  <td className="whitespace-nowrap px-1.5 py-1">
                                    {formatTime(row.created_at)}
                                  </td>
                                  <td className="whitespace-nowrap px-1.5 py-1 font-mono">
                                    {row.serial_number || row.device_id}
                                  </td>
                                  <td className="whitespace-nowrap px-1.5 py-1">{row.user_id ?? '—'}</td>
                                  <td className="whitespace-nowrap px-1.5 py-1">{row.score ?? '—'}</td>
                                  <td className="whitespace-nowrap px-1.5 py-1 text-slate-500">
                                    {row.kind === 'remind' ? 'повтор' : 'первый'}
                                  </td>
                                  <td className="px-1.5 py-1">
                                    <span
                                      className={`inline-flex rounded px-1 py-0.5 ${statusClass(row.status)}`}
                                      title={row.detail || ''}
                                    >
                                      {STATUS_LABEL[row.status] || row.status}
                                    </span>
                                  </td>
                                  <td className="whitespace-nowrap px-1.5 py-1">
                                    {row.opened_at ? formatTime(row.opened_at) : '—'}
                                  </td>
                                  <td
                                    className="max-w-[10rem] truncate px-1.5 py-1 text-slate-500"
                                    title={row.detail}
                                  >
                                    {row.detail || '—'}
                                  </td>
                                  <td className="px-1.5 py-1">
                                    <button
                                      type="button"
                                      aria-label="Текст пуша"
                                      title="Текст пуша"
                                      onClick={() => setPushDetail(row)}
                                      className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-[11px] leading-none text-slate-600 hover:border-sky-400 hover:text-sky-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300"
                                    >
                                      ℹ
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        )}
                      </table>
                    </div>
                    <HistoryPagination
                      page={historyPage}
                      pageSize={historyPageSize}
                      total={historyTotal}
                      visibleCount={historyLoading ? 0 : history.length}
                      isLoading={historyLoading}
                      onPageChange={(next) => setHistoryPage(next)}
                      onPageSizeChange={(next) => {
                        setHistoryPageSize(next);
                        setHistoryPage(1);
                      }}
                    />
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      )}
      {pushDetail ? <PushSendDetailModal row={pushDetail} onClose={() => setPushDetail(null)} /> : null}
    </div>
  );
}
